/************************************************************************************

* [UTILITY FUNCTIONS]

*************************************************************************************/
class LTA_SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${LTA_SectionId.#separator}${sectionName}`
	}
}

class LTA_HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		LTA_HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}

function l_disableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)
	focusableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1')
		element.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)

	focusableElements.forEach((element) => {
		element.removeAttribute('tabindex')
		element.removeAttribute('aria-disabled')
	})
}

function l_throttle(fn, delay) {
	let lastCall = 0
	let timeout = null

	return function (...args) {
		const now = Date.now()
		const remaining = delay - (now - lastCall)

		if (remaining <= 0) {
			if (timeout) {
				clearTimeout(timeout)
				timeout = null
			}
			lastCall = now
			fn(...args)
		} else if (!timeout) {
			timeout = setTimeout(() => {
				lastCall = Date.now()
				timeout = null
				fn(...args)
			}, remaining)
		}
	}
}

function l_addEventDelegate({
	context = document.documentElement,
	event = 'click',
	selector,
	handler,
	capture = false,
}) {
	const listener = function (e) {
		// loop parent nodes from the target to the delegation node
		for (let target = e.target; target && target !== this; target = target.parentNode) {
			if (target.matches(selector)) {
				handler.call(target, e, target)
				break
			}
		}
	}
	context.addEventListener(event, listener, capture)

	return () => {
		context.removeEventListener(event, listener, capture)
	}
}

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_stepChain(steps, onDone) {
	//   stepChain(
	//   [
	//     { action: () => console.log('A'), delay: 0 },
	//     { action: () => console.log('B'), delay: 300 },
	//   ],
	//   () => console.log('✅ Chain done')
	// )

	const timeouts = []
	let totalDelay = 0
	let isCancelled = false

	steps.forEach(({ action, delay }, index) => {
		totalDelay += delay
		const timeoutId = setTimeout(() => {
			if (isCancelled) return
			action()
			if (index === steps.length - 1 && onDone) onDone()
		}, totalDelay)
		timeouts.push(timeoutId)
	})

	return {
		cancel: () => {
			isCancelled = true
			timeouts.forEach(clearTimeout)
		},
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

function l_preloadImage(container) {
	const imageEls = container.querySelectorAll('img')

	imageEls.forEach((imageEl) => {
		if (imageEl.complete) return

		const image = new Image()
		image.src = imageEl.src
	})
}

function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

const l_trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const last = elements[elements.length - 1]
	const elementToFocus = defaultElementToFocus || first

	l_removeTrapFocus()

	l_trapFocusHandlers.focusin = (event) => {
		if (!container.contains(event.target)) return

		document.addEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key

		// On the last focusable element and tab forward, focus the first element.
		if ((event.target === last || last.contains(event.target)) && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || first.contains(event.target)) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', l_trapFocusHandlers.focusout)
	document.addEventListener('focusin', l_trapFocusHandlers.focusin)

	if (elementToFocus) {
		elementToFocus.focus()

		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}
}

function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
	document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
	document.removeEventListener('keydown', l_trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function l_pauseAllMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.pauseVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.pause())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	container.querySelectorAll('l-media-video').forEach((video) => {
		video?.player?.pause()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.pause()
	})
}

function l_playMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.playVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"play"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.play())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.play()
	})
	container.querySelectorAll('l-media-video').forEach((video) => {
		video?.player?.play()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.play()
	})
}

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.LTATheme.money.format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', ',')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function l_normalizeString(str) {
	return str
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLowerCase()
}

function l_handleize(str) {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.replace(/--+/g, '-')
}

function l_lockBodyScroll(elementName) {
	document.documentElement.classList.add('js-block-page')

	if (elementName) {
		document.documentElement.classList.add(`js-block-page-${elementName}`)
	}
}

function l_unLockBodyScroll(elementName) {
	if (elementName) {
		document.documentElement.classList.remove(`js-block-page-${elementName}`)

		const isExistAnotherBlockedClass = Array.from(document.documentElement.classList).some((cls) =>
			cls.startsWith('js-block-page-')
		)

		!isExistAnotherBlockedClass && document.documentElement.classList.remove('js-block-page')
	} else {
		document.documentElement.classList.remove('js-block-page')
	}
}

function l_setupVariablesBeforeOpenModal() {
	const announcementBarSection = document.getElementById('announcementBar')
	const headerSection = document.getElementById('header')
	const footerBottom = document.getElementById('footer-bottom')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection?.offsetHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${announcementBarSection?.offsetHeight || 0}px`
	)
	document.documentElement.style.setProperty('--l-footer-bottom-height', `${footerBottom?.offsetHeight || 0}px`)
}

function l_isElementInViewport(el) {
	const rect = el.getBoundingClientRect()

	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	)
}

function l_addClassesToRteTables() {
	document.querySelectorAll('.l-rte table, .shopify-policy__body .rte table').forEach((table) => {
		const wrapper = document.createElement('div')
		wrapper.classList.add('l-rte-table', 'l-overflow-x-auto', 'l-scrollbar--hidden')
		table.parentNode.insertBefore(wrapper, table)
		wrapper.appendChild(table)
	})
}

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor(cartDrawer = null) {
		this.cartDrawer = cartDrawer
	}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		config.body = new FormData(formEl)

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			formData.append(`items[${index}][id]`, item.id)
			formData.append(`items[${index}][quantity]`, item.quantity)
		})

		config.body = formData

		return config
	}

	async addToCart(config, id = 'default') {
		// Note: Don't use finally / catch here

		return fetch(`${window.LTATheme.routes.cart_add_url}.js`, config)
			.then((res) => res.json())
			.then((data) => {
				if (data.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async changeCartLineItem(formData) {
		return fetch(`${window.LTATheme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerEl = this.querySelector('[data-trigger]')
		this.contentEl = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.contentEl.removeAttribute('hidden')
			this.contentEl.classList.remove('l-hidden')
			this.classList.add(this.expandedClass)
			this.setExpandedAria()
		} else {
			this.contentEl.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria(null, false)
		}

		// Events
		this.triggerEl.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()

		this.setExpandedAria()
	}

	close() {
		this.selected = false
		this.collapse()

		this.setCollapsedAria()
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.contentEl.style.transition = '0'
						this.contentEl.style.height = 'auto'

						requestAnimationFrame(() => {
							this.contentEl.style.height = null
							this.contentEl.style.transition = null
						})
					})
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true

		this.contentEl.removeAttribute('hidden')
		this.contentEl.classList.remove('l-hidden')

		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)

		this.contentEl.style.height = this.contentEl.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}

				this.contentEl.setAttribute('hidden', 'hidden')
				this.contentEl.classList.add('l-hidden')
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.contentEl.style.transition = '0'
			this.contentEl.style.height = this.contentEl.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.contentEl.style.transition = null
				this.contentEl.style.height = this.collapsedHeight
			})
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
		this.contentEl && this.contentEl.removeAttribute('aria-hidden')
		this.contentEl && (this.contentEl.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	setExpandedAria() {
		this.setAttribute('open', true)

		this.triggerEl.setAttribute('aria-expanded', 'true')
		this.contentEl.removeAttribute('aria-hidden')
		this.contentEl.removeAttribute('tabindex')

		l_enableFocusableElements(this.contentEl)
		l_trapFocus(this)
	}

	setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
		this.removeAttribute('open')

		this.triggerEl.setAttribute('aria-expanded', 'false')
		this.contentEl.setAttribute('aria-hidden', 'true')
		this.contentEl.setAttribute('tabindex', '-1')

		l_disableFocusableElements(this.contentEl)
		enableTrapFocus && l_removeTrapFocus(elementToFocus)
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: SHOW MORE LESS
 ******************************************/

class LTAShowMoreLess extends LTACollapsibleTab {
	connectedCallback() {
		super.connectedCallback()

		this.setupHeight()
	}

	setupHeight() {}
}

customElements.define('l-show-more-less', LTAShowMoreLess)

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Check if Swiper is already loaded
		if (window.Swiper) {
			this.setupCarousel()

			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	setupCarousel() {
		this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

		Shopify.designMode && this.designModeScroll()
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl == swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		this.swiperCarousel.slideTo(index)
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: COMPARISON IMAGES
 ******************************************/
class LTAComparisonImages extends HTMLElement {
	constructor() {
		super()

		this.inputRange = this.querySelector('input[type="range"]')

		this.inputRange.addEventListener('input', () => {
			this.style.setProperty('--l-percent', `${this.inputRange.value}%`)
		})
	}
}

customElements.define('l-comparison-images', LTAComparisonImages)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.addEventListener('click', this.initPlayer.bind(this))
	}

	initPlayer(event) {
		event.preventDefault()

		const templateEl = this.querySelector('template')
		const isLoaded = this.classList.contains('is-loaded')

		if (!templateEl || isLoaded) return

		// Add template content to the element
		const templateContentEl = templateEl.content.cloneNode(true)

		this.appendChild(templateContentEl)
		this.classList.add('is-loaded')
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]: SELECT COMPONENT
 ******************************************/

class LTASelectComponent extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerButtonEl = this.querySelector('[data-button-trigger]')
		this.triggerButtonContentEl = this.querySelector('[data-button-content]') || this.triggerButtonEl
		this.selectModalEl = this.querySelector('[data-modal]')
		this.selectModalContainerEl = this.querySelector('[data-modal-container]')
		this.modalCloseEls = this.querySelectorAll('[data-modal-close]')

		this.nativeSelectEl = this.querySelector('select')
		this.selectOptionEls = this.querySelectorAll('[data-option-value]')

		// Insert modal to body
		// document.body.appendChild(this.selectModalEl)

		// Open / Close events
		this.triggerButtonEl.addEventListener('click', () => {
			this.open()
		})

		this.modalCloseEls.forEach((modalCloseEl) => {
			modalCloseEl.addEventListener('click', () => {
				this.close()
			})
		})

		// Update default events
		this.changeEvent = new Event('change', { bubbles: true })
		this.setDefaultValue()

		this.nativeSelectEl.addEventListener('change', () => {
			this.setDefaultValue()
		})

		// Close events
		this.selectOptionEls.forEach((selectOptionEl) => {
			selectOptionEl.addEventListener('click', () => {
				this.nativeSelectEl.value = selectOptionEl.dataset.optionValue
				this.nativeSelectEl.dispatchEvent(this.changeEvent)

				this.close()
			})
		})
	}

	open() {
		const isMobile = window.matchMedia('(max-width: 990px)').matches

		if (isMobile) l_lockBodyScroll()

		this.triggerButtonEl.classList.add('is-active')
		this.selectModalEl.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.selectModalEl.classList.add('is-active')
		})

		l_trapFocus(this.selectModalContainerEl, this.triggerButtonEl)

		// Set global events
		window.addEventListener('click', this.closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.closeOutsideOnEscape.bind(this))
	}

	close() {
		if (!this.selectModalEl.classList.contains('is-active')) return

		const isMobile = window.matchMedia('(max-width: 990px)').matches

		if (isMobile) l_unLockBodyScroll()

		this.triggerButtonEl.classList.remove('is-active')
		this.selectModalEl.classList.remove('is-active')

		window.l_debounce(() => {
			this.selectModalEl.classList.add('l-hidden')
		}, 400)()

		l_removeTrapFocus(this.triggerButtonEl)

		// Remove global events
		window.removeEventListener('click', this.closeOutsideOnClick)
		window.removeEventListener('keyup', this.closeOutsideOnEscape)
	}

	closeOutsideOnClick(event) {
		const target = event.target
		const isOutsideClick = !this.triggerButtonEl.contains(target) && !this.selectModalEl.contains(target)

		if (isOutsideClick) this.close()
	}

	closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.close()
		}
	}

	setDefaultValue() {
		const selectedValue = this.nativeSelectEl.value
		const prevSelectedOption = Array.from(this.nativeSelectEl.options).find((option) => option.getAttribute('selected'))

		const currentSelectedOption = this.nativeSelectEl.selectedOptions[0]

		prevSelectedOption.removeAttribute('selected')
		prevSelectedOption.classList.remove('is-selected')

		if (currentSelectedOption) {
			currentSelectedOption.setAttribute('selected', 'selected')
			currentSelectedOption.classList.add('is-selected')
		}

		this.selectOptionEls.forEach((selectOptionEl) => {
			const currentOptionValue = selectOptionEl.dataset.optionValue
			selectOptionEl.classList.toggle('is-selected', currentOptionValue == selectedValue)

			if (currentOptionValue == selectedValue) {
				this.triggerButtonContentEl.innerHTML = selectOptionEl.innerHTML
			}
		})
	}
}

customElements.define('l-select-component', LTASelectComponent)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTAProductModalExternalMedia extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.templateEl = this.querySelector('template')
		this.containerEl = this.querySelector('[data-media-container]')
	}

	playVideo() {
		if (this.templateEl) {
			this.templateContentEl = this.templateEl.content.cloneNode(true)
		}

		this.appendChild(this.templateContentEl)
	}

	pauseVideo() {
		this.innerHTML = '' // Clear the container to stop the video
	}
}

customElements.define('l-external-media', LTAProductModalExternalMedia)

/*****************************************
 * [COMPONENT]:-DRAWER
 *****************************************/

class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null

		if (this.dataset.closeSelector) {
			const externalCloseBtnEls = document.querySelectorAll(this.dataset.closeSelector)
			this.closeBtnEls = Array.from(this.closeBtnEls).concat(Array.from(externalCloseBtnEls))
		}

		// Data
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		// Setup events

		// Close this drawer if another drawer is open
		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', (event) => {
				if (event.detail.drawerName !== this.drawerName) {
					this.drawer_close()
				}
			})
		}

		// Open event
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				this.drawer_open(triggerEl)
			})
		})

		// Close event
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
			l_trapFocus(this.drawerInnerEl)
		}

		// Set up accessibility
		this.setAttribute('aria-expanded', 'true')
		this.removeAttribute('tabindex')

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		l_unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove accessibility
		this.setAttribute('aria-expanded', 'false')
		this.setAttribute('tabindex', '-1')

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		if (!this.classList.contains('is-active')) return

		const target = event.target
		const triggerSelector = this.dataset.drawerTriggerSelector || 'l-drawer-trigger'
		const isDrawerTrigger = event.target.closest(triggerSelector) || event.target.tagName == triggerSelector

		const isOutsideClick =
			!this.drawerInnerEl.contains(target) &&
			!Array.from(this.triggerEls).some((el) => el.contains(target)) &&
			!isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}
}

customElements.define('l-drawer', LTADrawer)

/*****************************************
 * [COMPONENT]: DRAWER TRIGGER
 ******************************************/

class LTADrawerTrigger extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.drawerEl = document.querySelector(this.dataset.selector)

		// Events
		if (this.drawerEl) {
			this.addEventListener('click', () => {
				this.drawerEl?.drawer_open?.(this)
			})
		}
	}
}

customElements.define('l-drawer-trigger', LTADrawerTrigger)

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

if (!customElements.get('l-atc-button')) {
	customElements.define(
		'l-atc-button',
		class LTACTAButton extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.buttonEl = this.querySelector('button')

				// Events
				this.buttonEl.addEventListener('click', this.handleAtcBtnClick.bind(this))
			}

			handleAtcBtnClick() {
				this.buttonEl.classList.add('is-loading', 'l-button-loading')

				const formData = ltaCartService.createConfigFromItems([
					{ id: this.dataset.variantId, quantity: parseInt(this.dataset.quantity) },
				])

				ltaCartService
					.addToCart(formData, 'l-atc-button')
					.then((res) => {})
					.finally(() => {
						this.buttonEl.classList.remove('is-loading', 'l-button-loading')
					})
			}
		}
	)
}

/*****************************************
 * [COMPONENT]: COUNTRY SELECTOR
 ******************************************/

class LTAHeaderMenuLocalization extends LTACollapsibleTab {
	constructor() {
		super()
	}

	connectedCallback() {
		super.connectedCallback()

		// Elements
		this.formEl = this.querySelector('form')
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.emptyTextEl = this.querySelector('[data-empty-text]')

		// Events
		this.addEventListener('click', this.handleCountryChange.bind(this))
		this.setupSearch()
		this.setupCloseEvents()
	}

	open() {
		super.open()

		if (this.searchInputEl) this.searchInputEl.focus()
	}

	setupCloseEvents() {
		document.addEventListener('keyup', (event) => {
			if (event.code.toUpperCase() === 'ESCAPE' && this.classList.contains('is-expanded')) {
				this.close()
			}
		})

		document.addEventListener('click', (event) => {
			if (!this.contains(event.target) && this.classList.contains('is-expanded')) {
				this.close()
			}
		})
	}

	setupSearch() {
		const countries = this.querySelector('[data-selections]')

		if (!this.searchInputEl || !countries) return

		this.searchInputEl.addEventListener('input', (event) => {
			const searchValue = event.target.value

			if (!searchValue) {
				countries.querySelectorAll('[data-id]').forEach((country) => {
					country.classList.remove('l-hidden')
				})

				this.emptyTextEl.classList.add('l-hidden')

				return
			}

			let hasResults = false

			countries.querySelectorAll('[data-id]').forEach((country) => {
				const searchQuery = l_handleize(searchValue)
				const countryName = l_handleize(country.dataset.text)
				const countryId = l_handleize(country.dataset.id)
				const isVisible = countryName.includes(searchQuery) || countryId.includes(searchQuery)

				country.classList.toggle('l-hidden', !isVisible)

				if (isVisible) hasResults = true
			})

			this.emptyTextEl.classList.toggle('l-hidden', hasResults)
		})
	}

	handleCountryChange(event) {
		const country = event.target.closest('[data-id]')
		if (!country) return

		this.formEl.querySelector('input[name="country_code"], input[name="language_code"]').value = country.dataset.id

		this.formEl.submit()
		country.dispatchEvent(new Event('submit', { bubbles: true }))
	}
}

customElements.define('l-localization-selector', LTAHeaderMenuLocalization)

/************************************************************************************

* RUN GLOBAL FUNCTIONALITIES / RUN BASE FUNCTIONALITIES

*************************************************************************************/

window.addEventListener('DOMContentLoaded', () => {
	// Initialize
	l_addClassesToRteTables()
	l_setupVariablesBeforeOpenModal()

	// Back to top
	const backToTopBtnEls = document.querySelectorAll('[data-back-to-top]')

	if (backToTopBtnEls.length) {
		backToTopBtnEls.forEach((backToTopBtnEl) => {
			backToTopBtnEl.addEventListener('click', () => {
				window.scrollTo({ top: 0, behavior: 'smooth' })
			})
		})
	}
})

window.addEventListener(
	'resize',
	l_debounce(() => {
		// Update variables
		l_setupVariablesBeforeOpenModal()
	}, 30)
)

if (window.visualViewport) {
	window.visualViewport.addEventListener(
		'resize',
		l_debounce(() => {
			l_setupVariablesBeforeOpenModal()
		}, 30)
	)
}

/*****************************************
 * HANDLE UPDATE CART COUNT
 ******************************************/

document.addEventListener('l:product:added', (event) => {
	fetch(`${window.LTATheme.routes.cart_url}.js`)
		.then((res) => res.json())
		.then((cartData) => {
			const cartCountEls = document.querySelectorAll('[data-cart-count]')
			cartCountEls.forEach((cartCountEl) => {
				cartCountEl.innerHTML = cartData.item_count
			})
		})
})

document.addEventListener('l:cart:updated', (event) => {
	const cartData = event.detail.cart

	const cartCountEls = document.querySelectorAll('[data-cart-count]')
	cartCountEls.forEach((cartCountEl) => {
		cartCountEl.innerHTML = cartData.item_count
	})
})

